class GeolocationPopup extends HTMLElement {
  constructor() {
    super();

    this.cookieName = 'beyours:geolocation';
    this.mode = this.dataset.mode || 'enabled';
    this.behavior = this.dataset.behavior || 'popup';
    this.expiryDays = Number(this.dataset.expiry) || 7;
    this.currentCountry = this.dataset.currentCountry || '';
    this.debugMode = this.dataset.debug === 'true';
    this.selectedIso = this.currentCountry;

    this.popup = this.querySelector('.promo-popup');
    this.overlay = this.querySelector('[data-geo-overlay]');
    this.form = this.querySelector('form');
    this.countryInput = this.querySelector('[data-geo-country-input]');
    this.languageInput = this.querySelector('[data-geo-language-input]');

    this.countryDropdown = this.querySelector('[data-geo-country-dropdown]');
    this.countryTrigger = this.querySelector('[data-geo-country-trigger]');
    this.countryTriggerLabel = this.querySelector('[data-geo-country-trigger-label]');
    this.countryList = this.querySelector('[data-geo-country-list]');
    this.countryOptions = Array.from(this.querySelectorAll('[data-geo-country-option]'));

    this.languageField = this.querySelector('[data-geo-language-field]');
    this.languageSelect = this.querySelector('[data-geo-language-select]');
    this.previewFlag = this.querySelector('[data-geo-preview-flag]');
    this.triggerFlag = this.querySelector('[data-geo-country-flag]');
    this.shopNowButton = this.querySelector('[data-geo-shop-now]');

    if (this.countryTrigger) {
      this.countryTrigger.addEventListener('click', this.toggleCountryList.bind(this));
      this.countryTrigger.addEventListener('focusout', this.onCountryDropdownFocusOut.bind(this));
    }
    this.countryOptions.forEach((option) => {
      option.addEventListener('click', () => this.selectCountry(option.dataset.isoCode));
    });
    if (this.countryDropdown) {
      this.countryDropdown.addEventListener('keyup', this.onCountryDropdownKeyUp.bind(this));
      this.countryDropdown.addEventListener('focusout', this.onCountryDropdownFocusOut.bind(this));
    }
    if (this.shopNowButton) this.shopNowButton.addEventListener('click', this.onShopNow.bind(this));
    if (this.overlay) this.overlay.addEventListener('click', this.onDecline.bind(this));
    this.addEventListener('keyup', this.onPopupKeyUp.bind(this));

    this.boundPositionCountryList = this.positionCountryList.bind(this);

    this.detectAndSuggest();
  }

  connectedCallback() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.onShopifySectionSelect = this.previewOpen.bind(this);
      this.onShopifySectionDeselect = this.previewClose.bind(this);
      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }

  disconnectedCallback() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
      this.hidePopup();
    }
  }

  previewOpen(event) {
    filterShopifyEvent(event, this, () => {
      this.applySelection(this.currentCountry);
      this.openPopup();
    });
  }

  previewClose(event) {
    filterShopifyEvent(event, this, this.hidePopup.bind(this));
  }

  log(...args) {
    if (this.debugMode) console.log('Geolocation Redirect:', ...args);
  }

  async detectAndSuggest() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return;

    if (this.mode !== 'testing' && this.getCookie(this.cookieName)) {
      this.log('Skipping detection, visitor already responded recently');
      return;
    }

    let data;
    try {
      const root = (window.theme && theme.routes && theme.routes.root_url) || '/';
      const base = root.endsWith('/') ? root : `${root}/`;
      const response = await fetch(`${base}browsing_context_suggestions.json`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        this.log('Suggestion request failed with status', response.status);
        return;
      }

      data = await response.json();
    } catch (error) {
      this.log('Suggestion request errored', error);
      return;
    }

    const detected = data && data.detected_values && data.detected_values.country;
    if (!detected || !detected.handle) {
      this.log('No country detected in response', data);
      return;
    }

    if (detected.handle === this.currentCountry) {
      this.log('Visitor is already viewing their detected market', detected.handle);
      return;
    }

    if (this.countryOptions.length === 0) {
      this.log('No enabled markets configured, nothing to offer');
      return;
    }

    const match = this.countryOptions.find((option) => option.dataset.isoCode === detected.handle);

    if (match && this.behavior === 'redirect') {
      // Unambiguous match and the merchant wants a silent switch, no popup needed.
      this.log('Detected market matches an enabled market, redirecting to', detected.handle);
      if (this.mode !== 'testing') this.setCookie(this.cookieName, this.expiryDays);
      this.switchCountry(detected.handle);
      return;
    }

    // Either the detected country matches one of our markets (suggest it), or it doesn't
    // match any of them (e.g. a visitor from Pakistan when only AU and NZ are configured) —
    // either way, show the popup with the best guess pre-selected and let the visitor confirm.
    const preselectedIso = match ? match.dataset.isoCode : this.currentCountry;
    this.log('Showing store picker, preselecting', preselectedIso);

    this.applySelection(preselectedIso);
    this.openPopup();
  }

  // Updates the trigger label/flag, the large preview flag, the aria-selected state of
  // every option, and the language field to reflect the given ISO code, without opening
  // or closing the list.
  applySelection(isoCode) {
    const option = this.countryOptions.find((o) => o.dataset.isoCode === isoCode);
    if (!option) return;

    this.selectedIso = isoCode;

    this.countryOptions.forEach((o) => {
      o.setAttribute('aria-selected', o === option ? 'true' : 'false');
    });

    if (this.countryTriggerLabel) this.countryTriggerLabel.textContent = option.dataset.name;

    const flagUrl = option.dataset.flagUrl;
    [this.previewFlag, this.triggerFlag].forEach((img) => {
      if (img && flagUrl) img.src = flagUrl;
    });

    this.updateLanguageField(option);
  }

  // Each country carries its own market's language list (see data-languages, built from
  // Liquid's country.available_languages), since a market can offer more than one language
  // independently of which market it is. Rebuilds the <select> for the newly picked country
  // and shows/hides the whole field depending on whether there's more than one language —
  // mirroring the same threshold the theme's own header language switcher uses.
  updateLanguageField(option) {
    if (!this.languageSelect || !this.languageField) return;

    let languages = [];
    try {
      languages = JSON.parse(option.dataset.languages || '[]');
    } catch (error) {
      languages = [];
    }

    if (languages.length <= 1) {
      this.languageField.hidden = true;
      return;
    }

    const previousValue = this.languageSelect.value;
    this.languageSelect.innerHTML = '';
    languages.forEach((language) => {
      const opt = document.createElement('option');
      opt.value = language.iso_code;
      opt.textContent = language.name;
      this.languageSelect.appendChild(opt);
    });

    // Keep the visitor's previous choice if it's still valid for this country, otherwise
    // fall back to the market's first (default) language.
    const stillValid = languages.some((language) => language.iso_code === previousValue);
    if (stillValid) this.languageSelect.value = previousValue;

    this.languageField.hidden = false;
  }

  selectCountry(isoCode) {
    this.applySelection(isoCode);
    this.closeCountryList();
    if (this.countryTrigger) this.countryTrigger.focus();
  }

  toggleCountryList() {
    if (!this.countryList) return;
    if (this.countryList.hidden) {
      this.openCountryList();
    } else {
      this.closeCountryList();
    }
  }

  openCountryList() {
    if (!this.countryList || !this.countryTrigger) return;
    this.countryList.hidden = false;
    this.countryTrigger.setAttribute('aria-expanded', 'true');
    this.positionCountryList();
    window.addEventListener('resize', this.boundPositionCountryList);
  }

  closeCountryList() {
    if (!this.countryList || !this.countryTrigger) return;
    this.countryList.hidden = true;
    this.countryTrigger.setAttribute('aria-expanded', 'false');
    window.removeEventListener('resize', this.boundPositionCountryList);
  }

  // The list is fixed-positioned against the viewport (not the popup card), so a short
  // mobile viewport can't clip it the way it would an absolutely/relatively positioned
  // list — it's always sized to whatever space is actually available, flipping to open
  // upward if there's more room above the trigger than below.
  positionCountryList() {
    if (!this.countryList || !this.countryTrigger) return;

    const triggerRect = this.countryTrigger.getBoundingClientRect();
    const margin = 8;
    const minUsableHeight = 120;
    const maxListHeight = 320;

    const spaceBelow = window.innerHeight - triggerRect.bottom - margin;
    const spaceAbove = triggerRect.top - margin;
    const openUpward = spaceBelow < minUsableHeight && spaceAbove > spaceBelow;

    this.countryList.style.left = `${triggerRect.left}px`;
    this.countryList.style.width = `${triggerRect.width}px`;
    this.countryList.style.maxHeight = `${Math.max(minUsableHeight, Math.min(maxListHeight, openUpward ? spaceAbove : spaceBelow))}px`;

    if (openUpward) {
      this.countryList.style.top = 'auto';
      this.countryList.style.bottom = `${window.innerHeight - triggerRect.top + margin}px`;
    } else {
      this.countryList.style.bottom = 'auto';
      this.countryList.style.top = `${triggerRect.bottom + margin}px`;
    }
  }

  onCountryDropdownKeyUp(event) {
    if (event.code && event.code.toUpperCase() === 'ESCAPE') {
      this.closeCountryList();
      if (this.countryTrigger) this.countryTrigger.focus();
    }
  }

  onCountryDropdownFocusOut(event) {
    // Only close once focus has actually left the whole dropdown (trigger + list),
    // not when it merely moves from the trigger button to one of the list options.
    if (this.countryDropdown && this.countryDropdown.contains(event.relatedTarget)) return;
    this.closeCountryList();
  }

  // A second layer of Escape handling on the popup itself: closes the whole popup when
  // the country list isn't the thing currently open (that case is handled above).
  onPopupKeyUp(event) {
    if (!event.code || event.code.toUpperCase() !== 'ESCAPE') return;
    if (this.countryList && !this.countryList.hidden) return;
    this.onDecline();
  }

  openPopup() {
    if (!this.popup) return;
    setScrollbarWidth();
    document.body.classList.add('promo-popup--open');
    this.popup.classList.add('is-active');
  }

  hidePopup() {
    if (!this.popup) return;

    this.closeCountryList();
    this.popup.classList.add('is-closing');
    window.setTimeout(() => {
      this.popup.classList.remove('is-active', 'is-closing');
      document.body.classList.remove('promo-popup--open');
    }, 500);
  }

  onShopNow() {
    this.hidePopup();
    if (this.mode !== 'testing') this.setCookie(this.cookieName, this.expiryDays);
    if (this.selectedIso) this.switchCountry(this.selectedIso);
  }

  onDecline() {
    this.hidePopup();
    if (this.mode !== 'testing') this.setCookie(this.cookieName, this.expiryDays);
  }

  switchCountry(isoCode) {
    if (!this.form || !this.countryInput) return;
    this.countryInput.value = isoCode;

    if (this.languageInput && this.languageSelect && this.languageSelect.value) {
      this.languageInput.value = this.languageSelect.value;
    }

    this.form.submit();
  }

  getCookie(name) {
    const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return match ? match[2] : null;
  }

  setCookie(name, days) {
    document.cookie = `${name}=1; max-age=${days * 24 * 60 * 60}; path=/`;
  }
}
customElements.define('geolocation-popup', GeolocationPopup);
